"use client";

import { Poppins } from "next/font/google";
import "./globals.css";
import { Box, CircularProgress, ThemeProvider, CssBaseline, GlobalStyles } from "@mui/material";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import AuthWrapper from "@/components/AuthWrapper";
import { DealershipProvider, useDealership } from "@/contexts/DealershipContext";
import { LocaleProvider, useLocale } from "@/contexts/LocaleContext";
import { Suspense, useEffect, useMemo, useState } from "react";
import Footer from "@/components/Footer";
import PrintFooter from "@/components/PrintFooter";
import { FaviconHandler } from "@/components/FaviconHandler";
import { LicenseInfo } from "@mui/x-license";
import { GoogleAnalytics } from "@next/third-parties/google";

import { TourProvider } from "@reactour/tour";
import { usePathname } from "next/navigation";
import { I18nextProvider } from "react-i18next";
import { i1 } from "@/i18n";
import theme from "@/theme/theme";
import { createCadaTheme } from "@/theme/cada";

import { useIsVariant } from "@/hooks/useVariant";

LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI!);

const poppins = Poppins({
    variable: "--font-poppins",
    subsets: ["latin"],
    weight: ["300", "400", "500", "600", "700"],
});

const drawerWidth = 256;

function DynamicThemeProvider({ children }: { children: React.ReactNode }) {
    const isCada = useIsVariant("cada");
    const { currentLocale } = useLocale();
    const cadaTheme = useMemo(() => createCadaTheme(currentLocale), [currentLocale]);

    return (
        <ThemeProvider theme={isCada ? cadaTheme : theme}>
            <CssBaseline />
            <GlobalStyles
                styles={{
                    ".reactour__popover": {
                        borderRadius: 16,
                        maxWidth: "450px !important",
                        // backgroundColor: 'red !important',
                    },
                    ".reactour__content": {
                        padding: theme.spacing(1),
                    },
                    ".reactour__content > p:last-child": {
                        marginBottom: theme.spacing(0),
                    },
                }}
            />
            {children}
        </ThemeProvider>
    );
}

function LayoutContent({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const { participantInfo, isParticipantLoading } = useDealership();
    const isCada = useIsVariant("cada");
    const isAtd = useIsVariant("atd");

    if (isParticipantLoading || !participantInfo) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100vw",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", height: "100vh" }}>
            <Sidebar drawerWidth={drawerWidth} />

            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <div
                    id="content"
                    className="print-safe-layout"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                    }}
                >
                    {pathname !== "/dealer-group-benchmarking" && (
                        <Header
                            titleTx={
                                isCada
                                    ? "common.variants.cadaTitle"
                                    : isAtd
                                      ? "common.variants.atdTitle"
                                      : "common.variants.nadaTitle"
                            }
                        />
                    )}
                    <div className="print-content-wrapper" style={{ paddingBottom: 20 }}>
                        {children}
                    </div>
                    <Footer />
                </div>
            </Box>
        </Box>
    );
}

function ConditionalLayoutContent({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();

    // Check if current path should bypass the authenticated layout
    const isUnauthenticatedRoute =
        pathname === "/" ||
        pathname === "/forgot-password" ||
        pathname === "/reset-password" ||
        pathname === "/landing" ||
        pathname === "/enrollment" ||
        pathname === "/portal" ||
        pathname === "/survey" ||
        pathname === "/upload" ||
        pathname === "/industry-report" ||
        pathname === "/edit-participants" ||
        pathname === "/contact" ||
        pathname === "/privacy" ||
        pathname === "/faqs" ||
        pathname === "/sso";

    if (isUnauthenticatedRoute) {
        return <>{children}</>;
    }

    return <LayoutContent>{children}</LayoutContent>;
}

export default function RootLayout({
    children,
}: Readonly<{
    children: React.ReactNode;
}>) {
    const [favicon, setFavicon] = useState("/esiq_favicon.ico");
    const [tagID, setTagID] = useState("");

    const updateFavicon = (path: string) => {
        setFavicon(path);
    };
    const updateTagID = (tag: string) => {
        setTagID(tag);
    };

    return (
        <html lang="en">
            <head>
                <FaviconHandler url={favicon} />
            </head>
            {tagID && <GoogleAnalytics gaId={tagID} />}
            <body className={`${poppins.variable}`}>
                <Suspense
                    fallback={
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100vh",
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    }
                >
                    <I18nextProvider i18n={i1}>
                        <LocaleProvider>
                            <DealershipProvider>
                                <Favicon updateFavicon={updateFavicon} updateTagID={updateTagID} />
                                <DynamicThemeProvider>
                                    <TourProvider
                                        showBadge={false}
                                        showCloseButton={false}
                                        steps={[]}
                                    >
                                        <AuthWrapper>
                                            <ConditionalLayoutContent>
                                                {children}
                                            </ConditionalLayoutContent>
                                            <PrintFooter />
                                        </AuthWrapper>
                                    </TourProvider>
                                </DynamicThemeProvider>
                            </DealershipProvider>
                        </LocaleProvider>
                    </I18nextProvider>
                </Suspense>
            </body>
        </html>
    );
}

interface FaviconProps {
    updateFavicon: (path: string) => void;
    updateTagID: (tag: string) => void;
}
const Favicon = (props: FaviconProps) => {
    const { updateFavicon, updateTagID } = props;
    const isAtd = useIsVariant("atd");
    const isCada = useIsVariant("cada");

    useEffect(() => {
        if (typeof window !== "undefined") {
            if (isCada) {
                updateFavicon("/cada_favicon.png");
                updateTagID("G-XBRSZ63EM0");
            } else if (isAtd) {
                updateFavicon("/atd_favicon.ico");
                updateTagID("G-92X1XN1PNJ");
            } else {
                updateFavicon("/nada_favicon.ico");
                updateTagID("G-92X1XN1PNJ");
            }
        }
    }, [isAtd, isCada, updateFavicon, updateTagID]);
    return null;
};
